import { useState, useEffect, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { 
  Search, 
  RefreshCw, 
  Download, 
  Users, 
  Clock, 
  CheckCircle, 
  XCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Calendar,
  Building,
  Mail,
  User,
  ArrowLeft,
  Lock,
  Eye,
  EyeOff
} from "lucide-react"

interface Lead {
  id: number
  name: string
  email: string
  company: string
  selected_date: string
  selected_time: string
  status: 'new' | 'contacted' | 'qualified' | 'converted' | 'lost'
  notes: string
  source: string
  created_at: string
}

const sourceLabels: Record<string, { label: string; color: string }> = {
  'n8n-landing': { label: 'Demo Request', color: 'bg-[#9e3667]/10 text-[#9e3667]' },
  'resource-download:n8n-node': { label: 'Resource Download', color: 'bg-blue-100 text-blue-700' },
  'resource-download:n8n Node Documentation': { label: 'Resource Download', color: 'bg-blue-100 text-blue-700' },
}

const statusColors: Record<string, string> = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-yellow-100 text-yellow-700',
  qualified: 'bg-purple-100 text-purple-700',
  converted: 'bg-green-100 text-green-700',
  lost: 'bg-gray-100 text-gray-700',
}

const statusIcons: Record<string, React.ReactNode> = {
  new: <Clock className="h-3 w-3" />,
  contacted: <Users className="h-3 w-3" />,
  qualified: <CheckCircle className="h-3 w-3" />,
  converted: <CheckCircle className="h-3 w-3" />,
  lost: <XCircle className="h-3 w-3" />,
}

// Simple admin credentials - in production, use proper auth
const ADMIN_USERNAME = "vorro_admin"
const ADMIN_PASSWORD = "n8n_leads_2026"

export function LeadAdmin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [loginError, setLoginError] = useState("")
  const [isLoggingIn, setIsLoggingIn] = useState(false)

  const [leads, setLeads] = useState<Lead[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [apiError, setApiError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [sourceFilter, setSourceFilter] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalLeads, setTotalLeads] = useState(0)
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null)
  const [isUpdating, setIsUpdating] = useState(false)

  // Check if already logged in (session storage)
  useEffect(() => {
    const session = sessionStorage.getItem('vorro_admin_session')
    if (session === 'authenticated') {
      setIsAuthenticated(true)
    }
  }, [])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoggingIn(true)
    setLoginError("")

    // Simple validation - in production use proper auth
    await new Promise(resolve => setTimeout(resolve, 500)) // Simulate network delay

    if (username === ADMIN_USERNAME && password === ADMIN_PASSWORD) {
      sessionStorage.setItem('vorro_admin_session', 'authenticated')
      setIsAuthenticated(true)
    } else {
      setLoginError("Invalid username or password")
    }
    setIsLoggingIn(false)
  }

  const handleLogout = () => {
    sessionStorage.removeItem('vorro_admin_session')
    setIsAuthenticated(false)
    setUsername("")
    setPassword("")
  }

  const goBackToSite = () => {
    window.location.hash = ''
  }

  const fetchLeads = useCallback(async () => {
    setIsLoading(true)
    setApiError(null)
    try {
      const params = new URLSearchParams()
      if (statusFilter) params.append('status', statusFilter)
      if (searchQuery) params.append('search', searchQuery)
      if (sourceFilter) params.append('source', sourceFilter)
      params.append('page', currentPage.toString())
      params.append('per_page', '15')

      const response = await fetch(`https://vorro.net/wp-json/vorro-leads/v1/leads?${params.toString()}`)
      const data = await response.json()
      
      if (data.code === 'rest_no_route') {
        setApiError('WordPress plugin not installed. Please install and activate the Vorro Leads Manager plugin on your WordPress site.')
        setLeads([])
        return
      }
      
      setLeads(data.leads || [])
      setTotalPages(data.pages || 1)
      setTotalLeads(data.total || 0)
    } catch (error) {
      console.error('Error fetching leads:', error)
      setApiError('Unable to connect to the WordPress API. Please check your network connection and ensure the plugin is installed.')
      setLeads([])
    } finally {
      setIsLoading(false)
    }
  }, [statusFilter, searchQuery, sourceFilter, currentPage])

  useEffect(() => {
    if (isAuthenticated) {
      fetchLeads()
    }
  }, [fetchLeads, isAuthenticated])

  const handleStatusChange = async (leadId: number, newStatus: string) => {
    setIsUpdating(true)
    try {
      const response = await fetch(`https://vorro.net/wp-json/vorro-leads/v1/leads/${leadId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      })
      
      if (response.ok) {
        fetchLeads()
        if (selectedLead?.id === leadId) {
          setSelectedLead({ ...selectedLead, status: newStatus as Lead['status'] })
        }
      }
    } catch (error) {
      console.error('Error updating lead:', error)
    } finally {
      setIsUpdating(false)
    }
  }

  const exportCSV = () => {
    const headers = ['Name', 'Email', 'Company', 'Date', 'Time', 'Status', 'Source', 'Created At']
    const csvContent = [
      headers.join(','),
      ...leads.map(lead => [
        `"${lead.name}"`,
        `"${lead.email}"`,
        `"${lead.company}"`,
        `"${lead.selected_date}"`,
        `"${lead.selected_time}"`,
        `"${lead.status}"`,
        `"${lead.source || 'n8n-landing'}"`,
        `"${lead.created_at}"`,
      ].join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `vorro-leads-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    })
  }

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#f7f7f8] flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
            <div className="text-center mb-8">
              <button onClick={goBackToSite} className="inline-block mb-6">
                <img
                  src="https://vorro.net/logo/logo.webp"
                  alt="Vorro"
                  style={{ height: '40px', width: 'auto' }}
                />
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Admin Login</h1>
              <p className="text-gray-500 mt-2">Sign in to access lead management</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Username
                </label>
                <Input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username"
                  className="h-12"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className="h-12 pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {loginError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{loginError}</p>
                </div>
              )}

              <Button
                type="submit"
                disabled={isLoggingIn}
                className="w-full h-12 bg-[linear-gradient(135deg,#a63083,#bf406a)] hover:opacity-90 text-white font-medium"
              >
                {isLoggingIn ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <Lock className="h-4 w-4 mr-2" />
                    Sign In
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button 
                onClick={goBackToSite}
                className="text-sm text-gray-500 hover:text-[#9e3667] transition-colors"
              >
                Back to website
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#f7f7f8]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={goBackToSite} className="flex items-center">
                <img
                  src="https://vorro.net/logo/logo.webp"
                  alt="Vorro"
                  style={{ height: '28px', width: 'auto' }}
                />
              </button>
              <span className="text-gray-300">|</span>
              <span className="text-sm font-semibold text-gray-700">Lead Management</span>
            </div>
            <div className="flex items-center gap-4">
              <button 
                onClick={goBackToSite}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Site
              </button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="text-gray-600"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters & Actions */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="p-4 border-b border-gray-100">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="flex flex-1 gap-3 w-full sm:w-auto">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search leads..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-10 border-gray-200"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="h-10 px-3 border border-gray-200 rounded-md text-sm bg-white"
                >
                  <option value="">All Status</option>
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="qualified">Qualified</option>
                  <option value="converted">Converted</option>
                  <option value="lost">Lost</option>
                </select>
                <select
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                  className="h-10 px-3 border border-gray-200 rounded-md text-sm bg-white"
                >
                  <option value="">All Types</option>
                  <option value="demo">Demo Requests</option>
                  <option value="resource">Resource Downloads</option>
                </select>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchLeads}
                  className="h-10"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportCSV}
                  className="h-10"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-[#9e3667]" />
              </div>
            ) : apiError ? (
              <div className="text-center py-20">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-lg mx-auto">
                  <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
                  <p className="text-red-700 text-lg font-medium mb-2">API Connection Error</p>
                  <p className="text-red-600 text-sm">{apiError}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchLeads}
                    className="mt-4 border-red-300 text-red-600 hover:bg-red-50"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Retry Connection
                  </Button>
                </div>
              </div>
            ) : leads.length === 0 ? (
              <div className="text-center py-20">
                <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg font-medium">No leads found</p>
                <p className="text-gray-400 text-sm mt-1">
                  {totalLeads === 0 
                    ? "Leads will appear here once visitors book demos" 
                    : "Try adjusting your filters"}
                </p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Contact</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Company</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Type</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Scheduled</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Submitted</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr 
                      key={lead.id} 
                      className="border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer transition-colors"
                      onClick={() => setSelectedLead(lead)}
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full bg-[#faf5f7] flex items-center justify-center">
                            <User className="h-4 w-4 text-[#9e3667]" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{lead.name}</p>
                            <p className="text-xs text-gray-500">{lead.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Building className="h-4 w-4 text-gray-400" />
                          {lead.company || '-'}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        {(() => {
                          const source = lead.source || 'n8n-landing'
                          const sourceInfo = sourceLabels[source] || (source.startsWith('resource-download') 
                            ? { label: 'Resource Download', color: 'bg-blue-100 text-blue-700' }
                            : { label: 'Demo Request', color: 'bg-[#9e3667]/10 text-[#9e3667]' })
                          return (
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${sourceInfo.color}`}>
                              {sourceInfo.label}
                            </span>
                          )
                        })()}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          {formatDate(lead.selected_date)} {lead.selected_time && `at ${lead.selected_time}`}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[lead.status] || statusColors.new}`}>
                          {statusIcons[lead.status]}
                          {lead.status.charAt(0).toUpperCase() + lead.status.slice(1)}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-500">
                        {formatDate(lead.created_at)}
                      </td>
                      <td className="py-4 px-4">
                        <select
                          value={lead.status}
                          onChange={(e) => {
                            e.stopPropagation()
                            handleStatusChange(lead.id, e.target.value)
                          }}
                          onClick={(e) => e.stopPropagation()}
                          disabled={isUpdating}
                          className="text-xs border border-gray-200 rounded px-2 py-1.5 bg-white hover:border-gray-300 transition-colors"
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="qualified">Qualified</option>
                          <option value="converted">Converted</option>
                          <option value="lost">Lost</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-4 border-t border-gray-100 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Page {currentPage} of {totalPages} ({totalLeads} total leads)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Lead Detail Sidebar */}
      {selectedLead && (
        <div className="fixed inset-0 z-50 bg-black/30" onClick={() => setSelectedLead(null)}>
          <div 
            className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Lead Details</h2>
              <button
                onClick={() => setSelectedLead(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XCircle className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 rounded-full bg-[#faf5f7] flex items-center justify-center">
                  <User className="h-6 w-6 text-[#9e3667]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{selectedLead.name}</h3>
                  <p className="text-gray-500">{selectedLead.company || 'No company'}</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Mail className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <a href={`mailto:${selectedLead.email}`} className="text-sm text-[#9e3667] hover:underline">
                      {selectedLead.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Calendar className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Scheduled Demo</p>
                    <p className="text-sm text-gray-900">
                      {formatDate(selectedLead.selected_date)} {selectedLead.selected_time && `at ${selectedLead.selected_time}`}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Clock className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Submitted</p>
                    <p className="text-sm text-gray-900">{formatDate(selectedLead.created_at)}</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={selectedLead.status}
                  onChange={(e) => handleStatusChange(selectedLead.id, e.target.value)}
                  disabled={isUpdating}
                  className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm bg-white"
                >
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="qualified">Qualified</option>
                  <option value="converted">Converted</option>
                  <option value="lost">Lost</option>
                </select>
              </div>

              <div className="pt-4 border-t border-gray-100">
                <a
                  href={`mailto:${selectedLead.email}?subject=Your Vorro Demo Booking&body=Hi ${selectedLead.name},%0D%0A%0D%0AThank you for booking a demo with Vorro.%0D%0A%0D%0ABest regards,%0D%0AVorro Team`}
                  className="block w-full"
                >
                  <Button className="w-full bg-[linear-gradient(135deg,#a63083,#bf406a)] hover:opacity-90 text-white">
                    <Mail className="h-4 w-4 mr-2" />
                    Send Email
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
